import React, { useCallback, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './ServiceOptionGroupsBuilder.module.css';
import {
    MAX_GROUPS_PER_SERVICE,
    MAX_OPTIONS_PER_GROUP,
    type OptionGroupKind,
    type OptionGroupModel,
    type OptionRowModel,
} from '../../types/serviceOptionGroups';
import {
    makeEmptyAddOnGroup,
    makeEmptyOption,
} from '../../lib/serviceOptionGroups/serviceOptionGroups';

/**
 * PBI 1962 (AB#1968) — Salon-Owner service option-groups builder.
 *
 * Authors the backend's `OptionGroupsJson` (additive alongside the flat
 * `PricingOptionsJson`). Add/remove/reorder GROUPS and per-group OPTIONS.
 *
 * Rules enforced in the UI (mirroring the backend validators — the backend is
 * authoritative):
 *   - Exactly one Variant group per service (v1) — the Variant group's Type
 *     select is locked so the owner cannot create a second Variant group.
 *   - Variant option Price/Duration are ABSOLUTE; Add-on Price/Duration are
 *     ADDITIVE (+delta) — reflected in the field labels + a "+" price prefix.
 *   - required ≡ minSelect >= 1.
 *   - Caps: ≤ 8 groups/service, ≤ 20 options/group (add buttons disable at cap).
 *   - Single currency per service (the currency is owned by the parent modal
 *     and passed in read-only).
 *
 * A legacy auto-wrapped group (M54 backfill) renders a read-only
 * "Auto-wrapped (legacy)" badge but is otherwise fully editable.
 *
 * Fully controlled: `value` + `onChange` (the parent owns the model so it can
 * serialize on submit and gate the Save button on validity).
 */

export interface ServiceOptionGroupsBuilderProps {
    value: OptionGroupModel[];
    onChange: (next: OptionGroupModel[]) => void;
    /** Service-wide currency (single currency per service). Display only. */
    currency: string;
    disabled?: boolean;
    /**
     * i18n keys of validation errors to surface at the top of the builder
     * (deduped by the caller). Advisory — the backend re-validates.
     */
    errorKeys?: string[];
}

const KIND_OPTIONS: OptionGroupKind[] = ['Variant', 'AddOn'];

const ServiceOptionGroupsBuilder: React.FC<ServiceOptionGroupsBuilderProps> = ({
    value,
    onChange,
    currency,
    disabled = false,
    errorKeys = [],
}) => {
    const { t, isRTL } = useLanguage();

    const variantGroupKey = useMemo(
        () => value.find((g) => g.kind === 'Variant')?.key,
        [value],
    );

    /* ── group ops ──────────────────────────────────────────────── */
    const patchGroup = useCallback(
        (key: string, patch: Partial<OptionGroupModel>) => {
            onChange(value.map((g) => (g.key === key ? { ...g, ...patch } : g)));
        },
        [value, onChange],
    );

    const addGroup = useCallback(() => {
        if (value.length >= MAX_GROUPS_PER_SERVICE) return;
        onChange([...value, makeEmptyAddOnGroup()]);
    }, [value, onChange]);

    const removeGroup = useCallback(
        (key: string) => {
            onChange(value.filter((g) => g.key !== key));
        },
        [value, onChange],
    );

    const moveGroup = useCallback(
        (index: number, dir: -1 | 1) => {
            const target = index + dir;
            if (target < 0 || target >= value.length) return;
            const next = [...value];
            [next[index], next[target]] = [next[target], next[index]];
            onChange(next);
        },
        [value, onChange],
    );

    const setKind = useCallback(
        (key: string, kind: OptionGroupKind) => {
            // Switching TO Variant is blocked in the UI when a Variant already
            // exists (the select omits the option), so this only fires for a
            // legal transition. Keep Kind ↔ SelectionType consistent.
            if (kind === 'Variant') {
                patchGroup(key, {
                    kind,
                    selectionType: 'Single',
                    minSelect: 1,
                    maxSelect: 1,
                });
            } else {
                patchGroup(key, {
                    kind,
                    selectionType: 'Multiple',
                    minSelect: 0,
                    maxSelect: 3,
                });
            }
        },
        [patchGroup],
    );

    /* ── option ops ─────────────────────────────────────────────── */
    const patchOption = useCallback(
        (groupKey: string, optKey: string, patch: Partial<OptionRowModel>) => {
            onChange(
                value.map((g) =>
                    g.key === groupKey
                        ? {
                              ...g,
                              options: g.options.map((o) =>
                                  o.key === optKey ? { ...o, ...patch } : o,
                              ),
                          }
                        : g,
                ),
            );
        },
        [value, onChange],
    );

    const addOption = useCallback(
        (groupKey: string) => {
            onChange(
                value.map((g) =>
                    g.key === groupKey && g.options.length < MAX_OPTIONS_PER_GROUP
                        ? { ...g, options: [...g.options, makeEmptyOption()] }
                        : g,
                ),
            );
        },
        [value, onChange],
    );

    const removeOption = useCallback(
        (groupKey: string, optKey: string) => {
            onChange(
                value.map((g) =>
                    g.key === groupKey
                        ? { ...g, options: g.options.filter((o) => o.key !== optKey) }
                        : g,
                ),
            );
        },
        [value, onChange],
    );

    const numberOrZero = (raw: string): number => {
        const v = parseFloat(raw);
        return Number.isNaN(v) ? 0 : v;
    };
    const intOrZero = (raw: string): number => {
        const v = parseInt(raw, 10);
        return Number.isNaN(v) ? 0 : Math.max(0, v);
    };

    return (
        <div className={styles.builder} dir={isRTL ? 'rtl' : 'ltr'}>
            <p className={styles.intro}>{t('services.builder.intro')}</p>

            {errorKeys.length > 0 && (
                <div className={styles.errorBanner} role="alert">
                    <ul>
                        {errorKeys.map((k) => (
                            <li key={k}>{t(k)}</li>
                        ))}
                    </ul>
                </div>
            )}

            {value.length === 0 && (
                <div className={styles.empty}>{t('services.builder.empty')}</div>
            )}

            {value.map((group, gi) => {
                const isVariant = group.kind === 'Variant';
                const required = group.minSelect >= 1;
                const priceLabel = isVariant
                    ? t('services.builder.priceAbsolute')
                    : t('services.builder.priceDelta');
                const atOptionCap = group.options.length >= MAX_OPTIONS_PER_GROUP;
                // Only allow choosing "Variant" in the Type select for the group
                // that already IS the variant — prevents a 2nd Variant group.
                const kindChoices = KIND_OPTIONS.filter(
                    (k) => k !== 'Variant' || group.key === variantGroupKey,
                );

                return (
                    <div
                        key={group.key}
                        className={`${styles.group} ${isVariant ? styles.groupVariant : styles.groupAddon}`}
                    >
                        <div className={styles.groupHeader}>
                            <div className={styles.groupNameWrap}>
                                <label className={styles.microLabel}>
                                    {t('services.builder.groupName')}
                                </label>
                                <input
                                    type="text"
                                    className={styles.groupNameInput}
                                    value={group.name}
                                    placeholder={t('services.builder.groupNamePlaceholder')}
                                    onChange={(e) => patchGroup(group.key, { name: e.target.value })}
                                    disabled={disabled}
                                    maxLength={100}
                                    aria-label={t('services.builder.groupName')}
                                />
                            </div>
                            <div className={styles.groupNameWrap}>
                                <label className={styles.microLabel}>
                                    {t('services.builder.groupNameAr')}
                                </label>
                                <input
                                    type="text"
                                    className={styles.groupNameInput}
                                    dir="rtl"
                                    value={group.nameAr}
                                    onChange={(e) => patchGroup(group.key, { nameAr: e.target.value })}
                                    disabled={disabled}
                                    maxLength={100}
                                    aria-label={t('services.builder.groupNameAr')}
                                />
                            </div>

                            <div className={styles.badges}>
                                <span
                                    className={`${styles.pill} ${isVariant ? styles.pillVariant : styles.pillAddon}`}
                                >
                                    {isVariant
                                        ? t('services.builder.variant')
                                        : t('services.builder.addon')}
                                </span>
                                <span
                                    className={`${styles.pill} ${required ? styles.pillRequired : styles.pillOptional}`}
                                >
                                    {required
                                        ? t('services.builder.required')
                                        : t('services.builder.optional')}
                                </span>
                                {group.isImplicit && (
                                    <span
                                        className={`${styles.pill} ${styles.pillImplicit}`}
                                        title={t('services.builder.implicitBadge')}
                                    >
                                        {t('services.builder.implicitBadge')}
                                    </span>
                                )}
                            </div>

                            <div className={styles.groupActions}>
                                <button
                                    type="button"
                                    className={styles.iconBtn}
                                    onClick={() => moveGroup(gi, -1)}
                                    disabled={disabled || gi === 0}
                                    aria-label={t('services.builder.moveUp')}
                                    title={t('services.builder.moveUp')}
                                >
                                    ↑
                                </button>
                                <button
                                    type="button"
                                    className={styles.iconBtn}
                                    onClick={() => moveGroup(gi, 1)}
                                    disabled={disabled || gi === value.length - 1}
                                    aria-label={t('services.builder.moveDown')}
                                    title={t('services.builder.moveDown')}
                                >
                                    ↓
                                </button>
                                <button
                                    type="button"
                                    className={styles.removeGroupBtn}
                                    onClick={() => removeGroup(group.key)}
                                    disabled={disabled}
                                    aria-label={t('services.builder.removeGroup')}
                                    title={t('services.builder.removeGroup')}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Group rule controls */}
                        <div className={styles.groupRules}>
                            <div className={styles.ruleField}>
                                <label className={styles.microLabel}>
                                    {t('services.builder.kind')}
                                </label>
                                <select
                                    value={group.kind}
                                    onChange={(e) => setKind(group.key, e.target.value as OptionGroupKind)}
                                    disabled={disabled}
                                    aria-label={t('services.builder.kind')}
                                >
                                    {kindChoices.map((k) => (
                                        <option key={k} value={k}>
                                            {k === 'Variant'
                                                ? t('services.builder.variant')
                                                : t('services.builder.addon')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.ruleField}>
                                <label className={styles.microLabel}>
                                    {t('services.builder.selection')}
                                </label>
                                <select
                                    value={group.selectionType}
                                    onChange={(e) =>
                                        patchGroup(group.key, {
                                            selectionType: e.target.value as 'Single' | 'Multiple',
                                        })
                                    }
                                    disabled={disabled || isVariant}
                                    aria-label={t('services.builder.selection')}
                                >
                                    <option value="Single">
                                        {t('services.builder.selectionSingle')}
                                    </option>
                                    <option value="Multiple">
                                        {t('services.builder.selectionMultiple')}
                                    </option>
                                </select>
                            </div>
                            <div className={styles.ruleFieldNarrow}>
                                <label className={styles.microLabel}>
                                    {t('services.builder.minSelect')}
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    inputMode="numeric"
                                    value={group.minSelect}
                                    onChange={(e) =>
                                        patchGroup(group.key, { minSelect: intOrZero(e.target.value) })
                                    }
                                    disabled={disabled || isVariant}
                                    aria-label={t('services.builder.minSelect')}
                                />
                            </div>
                            <div className={styles.ruleFieldNarrow}>
                                <label className={styles.microLabel}>
                                    {t('services.builder.maxSelect')}
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    inputMode="numeric"
                                    value={group.maxSelect}
                                    onChange={(e) =>
                                        patchGroup(group.key, {
                                            maxSelect: Math.max(1, intOrZero(e.target.value)),
                                        })
                                    }
                                    disabled={disabled || isVariant}
                                    aria-label={t('services.builder.maxSelect')}
                                />
                            </div>
                        </div>

                        {/* Option rows */}
                        <div className={styles.optionsTable}>
                            <div className={styles.optionsHead}>
                                <span>{t('services.builder.optionName')}</span>
                                <span>{priceLabel}</span>
                                <span>{t('services.builder.duration')}</span>
                                <span aria-hidden="true" />
                            </div>
                            {group.options.map((opt) => (
                                <div key={opt.key} className={styles.optionRow}>
                                    <div className={styles.optNameCell}>
                                        <input
                                            type="text"
                                            className={styles.cellInput}
                                            value={opt.name}
                                            placeholder={t('services.builder.optionName')}
                                            onChange={(e) =>
                                                patchOption(group.key, opt.key, { name: e.target.value })
                                            }
                                            disabled={disabled}
                                            maxLength={100}
                                            aria-label={t('services.builder.optionName')}
                                        />
                                        <input
                                            type="text"
                                            className={styles.cellInputAr}
                                            dir="rtl"
                                            value={opt.nameAr}
                                            placeholder={t('services.builder.optionNameAr')}
                                            onChange={(e) =>
                                                patchOption(group.key, opt.key, { nameAr: e.target.value })
                                            }
                                            disabled={disabled}
                                            maxLength={100}
                                            aria-label={t('services.builder.optionNameAr')}
                                        />
                                    </div>
                                    <div className={styles.priceCell}>
                                        {/* Currency + numbers stay LTR-isolated even in RTL. */}
                                        <span className={styles.pricePrefix} dir="ltr">
                                            {isVariant ? '' : '+'}
                                            {currency}
                                        </span>
                                        <input
                                            type="number"
                                            className={styles.cellInput}
                                            dir="ltr"
                                            min={0}
                                            step="0.01"
                                            inputMode="decimal"
                                            value={opt.price || ''}
                                            placeholder="0"
                                            onChange={(e) =>
                                                patchOption(group.key, opt.key, {
                                                    price: numberOrZero(e.target.value),
                                                })
                                            }
                                            disabled={disabled}
                                            aria-label={priceLabel}
                                        />
                                    </div>
                                    <div className={styles.durationCell}>
                                        <input
                                            type="number"
                                            className={styles.cellInput}
                                            dir="ltr"
                                            min={0}
                                            step={5}
                                            inputMode="numeric"
                                            value={opt.durationMin || ''}
                                            placeholder={isVariant ? '0' : '+0'}
                                            onChange={(e) =>
                                                patchOption(group.key, opt.key, {
                                                    durationMin: intOrZero(e.target.value),
                                                })
                                            }
                                            disabled={disabled}
                                            aria-label={t('services.builder.duration')}
                                        />
                                        <span className={styles.durUnit}>
                                            {t('services.builder.durationMinutes')}
                                        </span>
                                    </div>
                                    <div className={styles.optActionCell}>
                                        {group.options.length > 1 && (
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => removeOption(group.key, opt.key)}
                                                disabled={disabled}
                                                aria-label={t('services.builder.removeOption')}
                                                title={t('services.builder.removeOption')}
                                            >
                                                🗑
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.groupFooter}>
                            <button
                                type="button"
                                className={styles.addOptionBtn}
                                onClick={() => addOption(group.key)}
                                disabled={disabled || atOptionCap}
                            >
                                + {t('services.builder.addOption')}
                            </button>
                            <span
                                className={`${styles.capCounter} ${atOptionCap ? styles.capWarn : ''}`}
                            >
                                {t('services.builder.optionsCount').replace(
                                    '{n}',
                                    String(group.options.length),
                                )}
                            </span>
                        </div>
                    </div>
                );
            })}

            <div className={styles.builderFooter}>
                <button
                    type="button"
                    className={styles.addGroupBtn}
                    onClick={addGroup}
                    disabled={disabled || value.length >= MAX_GROUPS_PER_SERVICE}
                >
                    + {t('services.builder.addGroup')}
                </button>
                <span
                    className={`${styles.capCounter} ${value.length >= MAX_GROUPS_PER_SERVICE ? styles.capWarn : ''}`}
                >
                    {t('services.builder.groupsCount').replace('{n}', String(value.length))}
                </span>
            </div>
        </div>
    );
};

export default ServiceOptionGroupsBuilder;
